import React, {
  forwardRef,
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { Document, Page, pdfjs } from 'react-pdf';
import { useAuthToken, useWindowSize } from '../../../hooks';
import { BrandStore, RegistrationStore } from '../../../context';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const Inner = styled.div`
  width: 100%;
  max-width: 100%;

  margin-left: auto;
  overflow-x: scroll;
  overflow-y: hidden;

  // center agreement on large screens (when it won't have to be scrolled)
  @media (min-width: 1000px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const SectionTitle = styled.div`
  border: 2px solid black;
  text-align: center;
  width: 100%;
  margin: 1rem auto 1rem auto;

  > * {
    margin: 2px;
  }
`;

interface SectionProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  title: string;
  type:
    | 'processor_agreements'
    | 'terms_and_conditions'
    | 'stax_promise'
    | 'stax_application'
    | 'merchant_company_information';
}

export const PdfSection: FunctionComponent<SectionProps> = forwardRef(
  (props, ref) => {
    const { title, type, ...restOfProps } = props;
    const {
      state: { brand },
    } = useContext(BrandStore);
    const { authToken } = useAuthToken();
    const {
      state: { registration },
    } = useContext(RegistrationStore);

    const merchantId = registration?.merchant_id;
    const windowSize = useWindowSize();
    const [pdfPageCount, setPdfPageCount] = useState(0);
    const [pdfWidth, setPdfWidth] = useState<number>(
      windowSize.isMobile ? 800 : 1000,
    );
    const innerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (innerRef.current) {
        let pdfWidth = innerRef.current.offsetWidth;
        pdfWidth = Math.min(pdfWidth, 1800);
        pdfWidth = Math.max(pdfWidth, 800);

        setPdfWidth(pdfWidth);
      }
    }, [windowSize]);

    const pdfDocumentFile = useMemo(() => {
      const requestedDocs =
        type === 'processor_agreements' ? brand?.processorAgreements : [type];

      const query = new URLSearchParams();

      requestedDocs?.forEach(doc => {
        query.append('documents', doc.toLowerCase().replace(/ /g, '_'));
      });

      return authToken &&
        merchantId &&
        requestedDocs &&
        requestedDocs.length > 0
        ? {
            url: `${
              process.env.REACT_APP_ONBOARDING_URL
            }/pdfs/${merchantId}?${query.toString()}`,
            httpHeaders: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        : null;
    }, [authToken, merchantId, brand?.processorAgreements, type]);

    return (
      <div
        {...restOfProps}
        ref={ref}
        data-testid={`${title.toLowerCase().replace(/ /g, '-')}-pdf`}
      >
        <SectionTitle>
          <h2>{title}</h2>
        </SectionTitle>
        <Inner>
          <Document
            loading={`Loading ${title}...`}
            error={`Unable to load ${title}`}
            file={pdfDocumentFile}
            onLoadSuccess={pdf => {
              // the docs reference numPages as a top level property on the 'pdf' object,
              // but it appears that it *actually* exists as a property within the '_pdfInfo' object
              // so, just to be safe, we'll cover both bases
              const pdfPageCount = pdf._pdfInfo?.numPages ?? pdf.numPages;
              setPdfPageCount(pdfPageCount);
            }}
          >
            {
              // render a Page for each pdf page
              Array(pdfPageCount)
                .fill(null)
                .map((_, i) => (
                  <Page
                    key={`${title.toLowerCase().replace(/ /g, '-')}-page-${
                      i + 1
                    }`}
                    width={pdfWidth}
                    pageNumber={i + 1}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                ))
            }
          </Document>
        </Inner>
      </div>
    );
  },
);
